import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { equipmentImage } from "@/lib/equipmentImage";
import {
  Wrench, Snowflake, Sparkles, Refrigerator, Camera, ChevronRight, ShieldCheck,
  ClipboardList, CalendarCheck, Truck, Cog, CheckCircle2, Box, CalendarClock,
} from "lucide-react";

const HERO_IMG = "/login-tech.jpg";

const QUICK = [
  { key: "instalacao", title: "Instalação de Ar-Condicionado", icon: Snowflake, cls: "bg-blue-50 text-[#1e7be0]" },
  { key: "manutencao", title: "Manutenção", icon: Wrench, cls: "bg-emerald-50 text-emerald-600" },
  { key: "higienizacao", title: "Higienização", icon: Sparkles, cls: "bg-violet-50 text-violet-600" },
  { key: "linhabranca", title: "Linha Branca", sub: "Geladeira, Freezer, Lavadora, Secadora e outros", icon: Refrigerator, cls: "bg-orange-50 text-orange-500" },
];

const STEPS = [
  { label: "Solicitado", icon: ClipboardList },
  { label: "Agendado", icon: CalendarCheck },
  { label: "Técnico a caminho", icon: Truck },
  { label: "Em atendimento", icon: Cog },
  { label: "Concluído", icon: CheckCircle2 },
];
const STATUS_STEP = { aberto: 0, atribuido: 1, aceito: 1, a_caminho: 2, concluido: 4 };

function equipIcon(type = "") {
  const t = type.toLowerCase();
  if (t.includes("ar")) return Snowflake;
  if (t.includes("gelad") || t.includes("freezer")) return Refrigerator;
  return Box;
}

export default function CustomerHome({ onAction, userName, requests = [], onSeeAll, onNav }) {
  const [equipment, setEquipment] = useState([]);
  useEffect(() => {
    api.get("/customer/equipment").then(({ data }) => setEquipment(data || [])).catch(() => {});
  }, []);

  const active = (requests || []).find((r) => !["cancelado", "concluido"].includes(r.status)) || (requests || [])[0];
  const activeIndex = active ? (STATUS_STEP[active.status] ?? 0) : -1;
  const first = userName ? userName.split(" ")[0] : "";

  const Section = ({ title, action, onAction: onAct, children }) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold tracking-wide text-slate-800 uppercase">{title}</h3>
        {action && (
          <button onClick={onAct} className="text-sm font-medium text-[#1e7be0] hover:underline flex items-center gap-0.5" data-testid={`see-all-${title.toLowerCase().replace(/\s/g, "-")}`}>
            {action} <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
      {children}
    </div>
  );

  return (
    <div data-testid="customer-home" className="space-y-6 pb-2">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0a2a6c] via-[#12409a] to-[#1e7be0] shadow-lg">
        <div className="relative z-10 p-6 max-w-[62%]">
          <p className="text-white text-xl font-bold leading-snug">Olá{first ? `, ${first}` : ""}! 👋</p>
          <p className="text-white/80 text-sm mt-1">Como podemos te ajudar hoje?</p>
        </div>
        <img src={HERO_IMG} alt="Técnico Clima Branca"
          className="pointer-events-none select-none absolute right-0 top-0 h-full w-[46%] object-cover object-center [mask-image:linear-gradient(to_right,transparent,black_30%)]" />
      </div>

      {/* Solicitar Assistência (card grande) */}
      <button onClick={() => onAction("assistencia")} data-testid="home-cta-assistencia"
        className="w-full text-left rounded-2xl bg-gradient-to-r from-[#0a2a6c] to-[#12409a] text-white px-5 py-4 flex items-center gap-4 shadow-md hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.99] transition-all">
        <span className="h-14 w-14 rounded-full bg-white flex items-center justify-center flex-shrink-0">
          <Wrench className="h-7 w-7 text-[#1e7be0]" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-bold text-lg leading-tight uppercase">Solicitar Assistência Técnica</span>
          <span className="block text-sm text-white/80 mt-0.5">Agende um técnico para sua casa ou empresa</span>
        </span>
        <ChevronRight className="h-6 w-6 text-white/80 flex-shrink-0" />
      </button>

      {/* Enviar problema */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-4 shadow-sm" data-testid="home-report-problem">
        <span className="h-12 w-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-6 w-6 text-[#1e7be0]" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 text-sm">Seu equipamento está com algum problema?</p>
          <p className="text-xs text-slate-500 mt-0.5">Conte o que aconteceu e envie fotos ou vídeos. Nossa equipe avalia e direciona o atendimento.</p>
        </div>
        <button onClick={() => onAction("enviar")} data-testid="home-send-problem-button"
          className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-xl border-2 border-[#1e7be0] text-[#1e7be0] px-3 py-2 text-sm font-semibold hover:bg-blue-50 transition-colors">
          <Camera className="h-4 w-4" /> Enviar
        </button>
      </div>

      {/* Serviços Rápidos */}
      <Section title="Serviços rápidos" action="Ver todos" onAction={() => onAction("assistencia")}>
        <div className="grid grid-cols-2 gap-3">
          {QUICK.map(({ key, title, sub, icon: Icon, cls }) => (
            <button key={key} onClick={() => onAction(key)} data-testid={`quick-${key}`}
              className="rounded-2xl border border-slate-200 bg-white p-4 text-center hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all">
              <span className={`h-12 w-12 rounded-xl ${cls} flex items-center justify-center mx-auto mb-2`}>
                <Icon className="h-6 w-6" />
              </span>
              <span className="block text-sm font-semibold text-slate-800 leading-tight">{title}</span>
              {sub && <span className="block text-[10px] text-slate-400 mt-1 leading-tight">{sub}</span>}
            </button>
          ))}
        </div>
      </Section>

      {/* Meu Chamado */}
      <Section title="Meu chamado" action="Ver todos" onAction={onSeeAll}>
        {active ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="home-my-request">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-blue-700 bg-blue-100 rounded px-2 py-0.5">
                {active.status === "concluido" ? "Concluído" : active.status === "cancelado" ? "Cancelado" : "Em andamento"}
              </span>
              {active.scheduled_date && (
                <span className="text-xs text-slate-500">Previsão: {active.scheduled_date}{active.scheduled_time ? ` às ${active.scheduled_time}` : ""}</span>
              )}
            </div>
            <p className="font-bold text-slate-800">Chamado #{String(active.id).slice(-6).toUpperCase()}</p>
            <div className="text-sm text-slate-600 space-y-0.5 mt-1">
              <p className="flex items-center gap-1.5"><Snowflake className="h-4 w-4 text-slate-400" /> {active.service_type}{active.equipment ? ` · ${active.equipment}` : ""}</p>
              {active.assigned_technician_name && <p className="flex items-center gap-1.5"><Cog className="h-4 w-4 text-slate-400" /> Técnico: {active.assigned_technician_name}</p>}
            </div>

            <div className="flex items-start mt-4 mb-3 overflow-x-auto">
              {STEPS.map(({ label, icon: Icon }, i) => {
                const done = activeIndex >= 0 && i <= activeIndex;
                const current = i === activeIndex;
                return (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center flex-shrink-0 w-16" data-testid={`home-step-${i}`}>
                      <span className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-colors ${done ? "bg-[#1e7be0] border-[#1e7be0] text-white" : "bg-slate-50 border-slate-200 text-slate-300"} ${current ? "ring-4 ring-blue-100" : ""}`}>
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className={`mt-1.5 text-[9px] text-center leading-tight ${done ? "text-slate-700 font-medium" : "text-slate-400"}`}>{label}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mt-4 min-w-4 rounded ${activeIndex > i ? "bg-[#1e7be0]" : "bg-slate-200"}`} />}
                  </React.Fragment>
                );
              })}
            </div>

            <button onClick={onSeeAll} data-testid="home-track-request"
              className="w-full rounded-xl border border-blue-200 text-[#1e7be0] font-semibold py-2.5 flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors">
              Acompanhar chamado <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center" data-testid="home-no-request">
            <ClipboardList className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Você ainda não tem chamados. Escolha um serviço acima para começar.</p>
          </div>
        )}
      </Section>

      {/* Meus Equipamentos */}
      <Section title="Meus equipamentos" action="Ver todos" onAction={() => onNav?.("equipamentos")}>
        {equipment.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-1" data-testid="home-equipment-list">
            {equipment.slice(0, 8).map((eq) => {
              const Icon = equipIcon(eq.equipment_type);
              const img = equipmentImage(eq.equipment_type);
              return (
                <div key={eq.id} className="flex-shrink-0 w-36 rounded-2xl border border-slate-200 bg-white p-3 text-center" data-testid={`home-equipment-${eq.id}`}>
                  <span className="h-16 w-full rounded-xl bg-slate-50 flex items-center justify-center mb-2 overflow-hidden">
                    {img ? <img src={img} alt={eq.equipment_type} className="h-full w-full object-contain" /> : <Icon className="h-8 w-8 text-[#1e7be0]" />}
                  </span>
                  <p className="text-sm font-semibold text-slate-800 leading-tight truncate">{eq.equipment_type}</p>
                  <p className="text-[11px] text-slate-500 leading-tight truncate">{[eq.brand, eq.model].filter(Boolean).join(" ") || eq.location || "—"}</p>
                  <span className="inline-block mt-1.5 text-[10px] font-medium text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">Em funcionamento</span>
                </div>
              );
            })}
          </div>
        ) : (
          <button onClick={() => onNav?.("equipamentos")} data-testid="home-add-equipment"
            className="w-full rounded-2xl border border-dashed border-slate-200 bg-white p-5 text-center hover:bg-slate-50 transition-colors">
            <Box className="h-7 w-7 text-slate-300 mx-auto mb-1.5" />
            <p className="text-sm text-slate-500">Cadastre seus equipamentos para acompanhar garantia e histórico.</p>
          </button>
        )}
      </Section>

      {/* Banner Manutenção Preventiva */}
      <button onClick={() => onAction("manutencao")} data-testid="home-preventive-banner"
        className="w-full text-left rounded-2xl bg-gradient-to-r from-[#0a2a6c] to-[#1e7be0] text-white p-5 flex items-center gap-4 shadow-md hover:shadow-lg transition-all">
        <ShieldCheck className="h-9 w-9 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-bold uppercase leading-tight">Agendar Manutenção Preventiva</p>
          <p className="text-xs text-white/80 mt-0.5">Evite problemas e aumente a vida útil do seu equipamento.</p>
        </div>
        <span className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-white/15 px-3 py-2 text-sm font-semibold backdrop-blur-sm">
          <CalendarClock className="h-4 w-4" /> Agendar agora
        </span>
      </button>
    </div>
  );
}
